import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  Calendar, 
  Link, 
  ExternalLink, 
  CheckCircle, 
  AlertTriangle,
  Info,
  Edit3
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BookingLinkManagerProps {
  applicationId: string;
  isApproved: boolean;
  onBookingLinkUpdated?: () => void;
}

export default function BookingLinkManager({
  applicationId,
  isApproved,
  onBookingLinkUpdated
}: BookingLinkManagerProps) {
  const [bookingLink, setBookingLink] = useState("");
  const [bookingNotes, setBookingNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentLink, setCurrentLink] = useState("");

  useEffect(() => {
    if (isApproved) {
      fetchCurrentBookingLink();
    }
  }, [applicationId, isApproved]);

  const fetchCurrentBookingLink = async () => {
    try {
      const { data, error } = await supabase
        .from("consultation_experts")
        .select("google_calendar_link")
        .eq("application_id", applicationId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error("❌ Error fetching booking link:", error);
        return;
      }

      if (data?.google_calendar_link) {
        setCurrentLink(data.google_calendar_link);
        setBookingLink(data.google_calendar_link);
      }
    } catch (error) {
      console.error("💥 Error fetching booking link:", error);
    }
  };

  const handleSaveBookingLink = async () => {
    if (!bookingLink.trim()) {
      toast.error("Please enter a booking link");
      return;
    }

    // Basic URL validation
    try {
      new URL(bookingLink);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("consultation_experts")
        .update({
          google_calendar_link: bookingLink.trim(),
          updated_at: new Date().toISOString()
        })
        .eq("application_id", applicationId);

      if (error) {
        console.error("❌ Error updating booking link:", error);
        toast.error("Failed to update booking link");
        return;
      }

      setCurrentLink(bookingLink.trim());
      setIsEditing(false);
      toast.success("Booking link updated successfully");
      onBookingLinkUpdated?.();
    } catch (error) {
      console.error("💥 Error updating booking link:", error);
      toast.error("Failed to update booking link");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setBookingLink(currentLink);
    setIsEditing(false);
  };

  if (!isApproved) {
    return (
      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" />
            Booking Link Setup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-amber-50 rounded-lg">
            <p className="text-sm text-amber-800">
              This application must be approved before you can set up the booking link.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasBookingLink = currentLink && currentLink.trim() !== "";

  return (
    <Card className="border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-800">
          <Calendar className="w-5 h-5" />
          Booking Link Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Status */}
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="space-y-1">
            <Label className="text-sm font-medium">Booking Link Status</Label>
            <div className="flex items-center gap-2">
              <Badge variant={hasBookingLink ? "default" : "secondary"} className="text-sm">
                {hasBookingLink ? (
                  <>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Configured
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Not Set
                  </>
                )}
              </Badge>
            </div>
          </div>
          {hasBookingLink && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(currentLink, '_blank')}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              Test Link
            </Button>
          )}
        </div>

        {/* Booking Link Display/Edit */}
        {!isEditing ? (
          <div className="space-y-3">
            {hasBookingLink ? (
              <div className="p-3 border rounded-lg bg-green-50 border-green-200">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="flex-1 space-y-2">
                    <p className="font-medium text-green-800">Booking Link Configured</p>
                    <div className="flex items-center gap-2">
                      <Link className="w-4 h-4 text-green-600" />
                      <a 
                        href={currentLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline break-all"
                      >
                        {currentLink}
                      </a>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="text-blue-600 hover:bg-blue-50"
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-3 border rounded-lg bg-orange-50 border-orange-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div className="flex-1 space-y-2">
                    <p className="font-medium text-orange-800">No Booking Link Set</p>
                    <p className="text-sm text-orange-700">
                      A booking link is required before this expert can be published to the marketplace.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="border-orange-200 text-orange-700 hover:bg-orange-50"
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Add Link
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 p-4 border rounded-lg bg-blue-50 border-blue-200">
            <div>
              <Label htmlFor="booking-link" className="text-sm font-medium">
                Booking Link *
              </Label>
              <Input
                id="booking-link"
                type="url"
                placeholder="https://calendar.google.com/calendar/u/0/appointments/schedules/..."
                value={bookingLink}
                onChange={(e) => setBookingLink(e.target.value)}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter the expert's Google Calendar booking link or other booking platform URL
              </p>
            </div>

            <div>
              <Label htmlFor="booking-notes" className="text-sm font-medium">
                Internal Notes (Optional)
              </Label>
              <Textarea
                id="booking-notes"
                placeholder="Add any notes about the booking setup..."
                value={bookingNotes}
                onChange={(e) => setBookingNotes(e.target.value)}
                className="mt-2"
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={handleSaveBookingLink}
                disabled={loading || !bookingLink.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? "Saving..." : "Save Booking Link"}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Help Information */}
        <div className="p-3 border rounded-lg bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="space-y-2">
              <p className="font-medium text-blue-800">Booking Link Requirements</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Must be a valid URL (Google Calendar, Calendly, etc.)</li>
                <li>• Required before expert can be published to marketplace</li>
                <li>• Clients will use this link to book consultations</li>
                <li>• Test the link to ensure it works correctly</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 